import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { NewsEditorState, NewsEditorAction } from '../types';

const initialState: NewsEditorState = {
  jobs: [],
  currentJob: null,
  isLoading: false,
  error: null,
  expandedStages: [],
};

function newsEditorReducer(state: NewsEditorState, action: NewsEditorAction): NewsEditorState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_JOBS':
      return { ...state, jobs: action.payload };

    case 'SET_CURRENT_JOB':
      return { ...state, currentJob: action.payload };

    case 'TOGGLE_STAGE_EXPANSION':
      return {
        ...state,
        expandedStages: state.expandedStages.includes(action.payload)
          ? state.expandedStages.filter(stage => stage !== action.payload)
          : [...state.expandedStages, action.payload]
      };

    case 'UPDATE_JOB_STATUS':
      return {
        ...state,
        jobs: state.jobs.map(job =>
          job.job_id === action.payload.jobId
            ? {
                ...job,
                stages: {
                  ...job.stages,
                  [action.payload.stage]: {
                    ...job.stages[action.payload.stage as keyof typeof job.stages],
                    ...action.payload.status
                  }
                }
              }
            : job
        ),
        currentJob: (() => {
          if (state.currentJob?.job_id === action.payload.jobId && state.currentJob) {
            const stageKey = action.payload.stage as keyof typeof state.currentJob.stages;
            return {
              ...state.currentJob,
              stages: {
                ...state.currentJob.stages,
                [stageKey]: {
                  ...state.currentJob.stages[stageKey],
                  ...action.payload.status
                }
              }
            };
          }
          return state.currentJob;
        })()
      };

    default:
      return state;
  }
}

interface NewsEditorContextType {
  state: NewsEditorState;
  dispatch: React.Dispatch<NewsEditorAction>;
}

const NewsEditorContext = createContext<NewsEditorContextType | undefined>(undefined);

export const useNewsEditor = () => {
  const context = useContext(NewsEditorContext);
  if (!context) {
    throw new Error('useNewsEditor must be used within a NewsEditorProvider');
  }
  return context;
};

interface NewsEditorProviderProps {
  children: ReactNode;
}

export const NewsEditorProvider: React.FC<NewsEditorProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(newsEditorReducer, initialState);

  return (
    <NewsEditorContext.Provider value={{ state, dispatch }}>
      {children}
    </NewsEditorContext.Provider>
  );
};

export default NewsEditorProvider;
